import React, { useMemo, useEffect, useRef, useState, useCallback } from "react";

interface BokehBackgroundProps {
  className?: string;
  /** Number of bokeh orbs */
  count?: number;
  /** Color of the orbs — supports any CSS color */
  color?: string;
}

interface Orb {
  id: number;
  size: number;
  x: number;
  y: number;
  opacity: number;
  duration: number;
  delay: number;
  driftX: number;
  driftY: number;
}

const BokehBackground: React.FC<BokehBackgroundProps> = ({
  className = "",
  count = 18,
  color = "rgba(218, 165, 32, 0.35)",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const displaceRef = useRef<{ x: number; y: number }[]>([]);
  const rafRef = useRef<number>();

  const orbs = useMemo<Orb[]>(() => {
    const result = Array.from({ length: count }, (_, i) => ({
      id: i,
      size: 60 + Math.random() * 250,
      x: -10 + Math.random() * 120,
      y: -10 + Math.random() * 120,
      opacity: 0.5 + Math.random() * 0.5,
      duration: 12 + Math.random() * 20,
      delay: -(Math.random() * 20),
      driftX: -50 + Math.random() * 100,
      driftY: -30 + Math.random() * 60,
    }));
    // Initialize displacement array
    displaceRef.current = result.map(() => ({ x: 0, y: 0 }));
    return result;
  }, [count]);

  const [displacements, setDisplacements] = useState<{ x: number; y: number }[]>(
    () => orbs.map(() => ({ x: 0, y: 0 }))
  );

  const animate = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      rafRef.current = requestAnimationFrame(animate);
      return;
    }

    const rect = container.getBoundingClientRect();
    const mouse = mouseRef.current;
    const influence = 120; // radius of mouse influence in px
    const strength = 40; // max push distance in px
    const decay = 0.08; // how fast displacement relaxes back

    let changed = false;

    orbs.forEach((orb, i) => {
      const orbCenterX = (orb.x / 100) * rect.width;
      const orbCenterY = (orb.y / 100) * rect.height;

      let targetX = 0;
      let targetY = 0;

      if (mouse.active) {
        const mx = mouse.x - rect.left;
        const my = mouse.y - rect.top;
        const dx = orbCenterX - mx;
        const dy = orbCenterY - my;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < influence + orb.size * 0.5) {
          const force = Math.max(0, 1 - dist / (influence + orb.size * 0.5));
          const angle = Math.atan2(dy, dx);
          targetX = Math.cos(angle) * force * strength;
          targetY = Math.sin(angle) * force * strength;
        }
      }

      const cur = displaceRef.current[i];
      const newX = cur.x + (targetX - cur.x) * decay;
      const newY = cur.y + (targetY - cur.y) * decay;

      if (Math.abs(newX - cur.x) > 0.05 || Math.abs(newY - cur.y) > 0.05) {
        changed = true;
      }

      displaceRef.current[i] = { x: newX, y: newY };
    });

    if (changed) {
      setDisplacements([...displaceRef.current]);
    }

    rafRef.current = requestAnimationFrame(animate);
  }, [orbs]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY, active: true };
    };
    const handleMouseLeave = () => {
      mouseRef.current = { ...mouseRef.current, active: false };
    };
    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <div ref={containerRef} className={`overflow-hidden pointer-events-none ${className}`}>
      {orbs.map((orb, i) => (
        <div
          key={orb.id}
          className="absolute rounded-full"
          style={{
            width: orb.size,
            height: orb.size,
            left: `${orb.x}%`,
            top: `${orb.y}%`,
            background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
            opacity: orb.opacity,
            filter: `blur(${orb.size * 0.15}px)`,
            animation: `bokeh-float ${orb.duration}s ease-in-out ${orb.delay}s infinite`,
            ["--drift-x" as string]: `${orb.driftX}px`,
            ["--drift-y" as string]: `${orb.driftY}px`,
            translate: `${displacements[i]?.x ?? 0}px ${displacements[i]?.y ?? 0}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes bokeh-float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(var(--drift-x), var(--drift-y)) scale(1.05); }
          66% { transform: translate(calc(var(--drift-x) * -0.5), calc(var(--drift-y) * -0.7)) scale(0.95); }
        }
      `}</style>
    </div>
  );
};

export default BokehBackground;
